/**
 * Viewing and downloading files that belong to a document (asset).
 *
 * Why this exists instead of the shared `useFilePreviewWithPresigned`: that hook resolves a URL
 * from a raw S3 key via the drive presign endpoint, which accepts any key and therefore cannot
 * honour a document's download restriction. The asset API no longer returns the key at all — it
 * returns a file id that its own endpoint authorises. So assets resolve URLs their own way while
 * the shared hook stays untouched for every other module.
 */

import { useCallback } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useFilePreview } from '@/contexts/file-preview'
import { OFFICE_EXTENSIONS } from '@/hooks/useFilePreviewWithPresigned'
import { downloadFile } from '@/utils/function'
import { getAssetFileUrl } from '../services'
import { AssetFile } from '../types'

const isOfficeFile = (fileName: string): boolean =>
    OFFICE_EXTENSIONS.includes(fileName.split('.').pop()?.toLowerCase() || '')

export const useAssetFileAccess = () => {
    const { t } = useTranslation()
    const { preview } = useFilePreview()

    /**
     * Open the file in the in-app preview dialog.
     *
     * `canDownload` only decides whether the dialog offers its own download button; viewing
     * itself is never blocked by the restriction.
     */
    const openAssetFile = useCallback(
        async (file: AssetFile, canDownload: boolean) => {
            try {
                const res = await getAssetFileUrl(file.asset_file_id, 'view')
                const url = res?.result
                if (!url) throw new Error('empty-url')

                const fileName = file.file_name || 'file'

                preview(
                    isOfficeFile(fileName) ? encodeURIComponent(url) : url,
                    // Withholding the download URL is what greys out the dialog's own
                    // download button for a restricted document.
                    canDownload ? url : undefined,
                    {
                        attachment_file_name: fileName,
                        attachment_file_url: url,
                        attachment_file_type: file.mime_type,
                        attachment_file_size: file.file_size || 0,
                    }
                )
            } catch (error) {
                console.error('Failed to open asset file:', error)
                toast.error(t('error-getting-file'))
            }
        },
        [preview, t]
    )

    /**
     * Save a copy of the file. The server re-checks the restriction, so this stays correct even
     * if the UI state is stale.
     */
    const downloadAssetFile = useCallback(
        async (file: AssetFile) => {
            try {
                const res = await getAssetFileUrl(
                    file.asset_file_id,
                    'download'
                )
                const url = res?.result
                if (!url) throw new Error('empty-url')

                downloadFile(url, file.file_name || 'file')
            } catch (error) {
                console.error('Failed to download asset file:', error)
                toast.error(t('error-downloading-file'))
            }
        },
        [t]
    )

    return { openAssetFile, downloadAssetFile }
}

export default useAssetFileAccess
